import {
  ActivityIcon,
  BookOpenIcon,
  CalendarIcon,
  ChartNetworkIcon,
  DatabaseIcon,
  EyeIcon,
  LayersIcon,
  LayoutDashboardIcon,
  LineChartIcon,
  ListIcon,
  MessageSquareIcon,
  MousePointerClickIcon,
  NewspaperIcon,
  SplitIcon,
  TrendingUpIcon,
  ZapIcon,
} from 'lucide-react'
import { type ComponentType, useId } from 'react'

import { FullscreenOverlay } from '@/components/widget/fullscreen-overlay'
import TabGroupedChart from '@/features/chart/components/tab-grouped-chart'
import { lazyWidget } from '@/features/dashboard/lib/lazy-widget'
import { type ComponentKey } from '@/features/dashboard/lib/widget-sizes'

// Code-split every widget but the chart: the page shell imports this registry, so a
// static import here lands in the initial bundle whether or not the page renders it.
// lazyWidget, not next/dynamic, so the first mount doesn't suspend (see lazy-widget.tsx).
// `= {}` default params type these as `Props | undefined`, which withFullscreen rejects.
const EconomicCalendarWidget = lazyWidget<{ cardId?: string }>(
  () => import('@/features/dashboard/components/economic-calendar')
)
const AccountCards = lazyWidget(
  () => import('@/features/dashboard/components/widgets/account-cards')
)
const DataWidget = lazyWidget(() => import('@/features/dashboard/components/widgets/data-widget'))
const NewsfeedWidget = lazyWidget(() => import('@/features/dashboard/components/widgets/newsfeed'))
const OrderBook = lazyWidget(() => import('@/features/dashboard/components/widgets/orderbook'))
const PositionsTabContents = lazyWidget<{ cardId?: string }>(
  () => import('@/features/dashboard/components/widgets/positions-tab-contents')
)
const PresetWidget = lazyWidget(
  () => import('@/features/dashboard/components/widgets/preset-widget')
)
const RecentTrades = lazyWidget(
  () => import('@/features/dashboard/components/widgets/recent-trades')
)
const SpreaderWidget = lazyWidget(() => import('@/features/dashboard/components/widgets/spreader'))
const TickerTape = lazyWidget(() => import('@/features/dashboard/components/widgets/ticker-tape'))
const WatchList = lazyWidget<{ cardId?: string }>(
  () => import('@/features/dashboard/components/widgets/watch-list')
)
const OneTapBuyWidget = lazyWidget(
  () => import('@/features/one-tap-buy/components/one-tap-buy-widget')
)
const OrderContainer = lazyWidget(() =>
  import('@/features/order/components/order-container').then((m) => m.OrderContainer)
)
const QuickOrderWidget = lazyWidget(
  () => import('@/features/quick-order/components/quick-order-widget')
)
const RiskSlidesWidget = lazyWidget(() =>
  import('@/features/risk-slides').then((m) => m.RiskSlidesWidget)
)

export type { ComponentDefaultSize, ComponentKey } from '@/features/dashboard/lib/widget-sizes'

/**
 * Universal HOC to wrap any dashboard widget with FullscreenOverlay.
 * Uses the cardId prop as the groupId for fullscreen state management.
 */
function withFullscreen<P extends { cardId?: string }>(Component: ComponentType<P>) {
  return function FullscreenWrapped(props: P) {
    const fallbackId = useId()
    const groupId = props.cardId ?? fallbackId
    return (
      <FullscreenOverlay groupId={groupId}>
        <Component {...props} />
      </FullscreenOverlay>
    )
  }
}

/**
 * Full-size frame around a registered widget. Widgets carry no elevation (STYLE_GUIDE.md: no
 * card shadows); the old light-mode glow was clipped by flush neighbours into a one-sided
 * shade.
 */
function withCardFrame<P extends object>(Component: ComponentType<P>) {
  return function CardFrame(props: P) {
    return (
      <div className="h-full w-full">
        <Component {...props} />
      </div>
    )
  }
}

export type WidgetCategory = 'market' | 'execution' | 'portfolio' | 'tools'

export interface ComponentConfig {
  key: ComponentKey
  icon: ComponentType<{ className?: string }>
  src: string
  label: string
  category: WidgetCategory
  component: ComponentType<any> // Flexible to accept various prop types
}

export const COMPONENT_REGISTRY: Record<ComponentKey, ComponentConfig> = {
  // 1) Price Chart
  'price-chart': {
    key: 'price-chart',
    icon: LineChartIcon,
    src: '/images/examples/dark/pie-chart.webp',
    label: 'Price Chart',
    category: 'market',
    component: withCardFrame(TabGroupedChart),
  },
  // 2) Orderbook
  'order-book': {
    key: 'order-book',
    icon: BookOpenIcon,
    src: '/images/examples/dark/order-book.webp',
    label: 'Order Book',
    category: 'market',
    component: withCardFrame(withFullscreen(OrderBook)),
  },
  // 4) Trades
  trades: {
    key: 'trades',
    icon: TrendingUpIcon,
    src: '/images/examples/dark/trades.webp',
    label: 'Trades',
    category: 'market',
    component: withCardFrame(withFullscreen(RecentTrades)),
  },
  // 5) Ticker
  ticker: {
    key: 'ticker',
    icon: ActivityIcon,
    src: '/images/examples/dark/ticker.webp',
    label: 'Ticker',
    category: 'market',
    component: withCardFrame(withFullscreen(TickerTape)),
  },
  // 6) Watch list
  watchlist: {
    key: 'watchlist',
    icon: EyeIcon,
    src: '/images/examples/dark/watch-list.webp',
    label: 'Watch List',
    category: 'market',
    component: withFullscreen(WatchList),
  },
  // 7) Positions / Orders / History
  positions: {
    key: 'positions',
    icon: ListIcon,
    src: '/images/examples/dark/positions-orders-history.webp',
    label: 'Positions / Orders / History',
    category: 'portfolio',
    component: withCardFrame(withFullscreen(PositionsTabContents)),
  },
  // 8) Order Form (key kept for saved layouts; chat moved to the assistant)
  'chat-order-form': {
    key: 'chat-order-form',
    icon: MessageSquareIcon,
    src: '/images/examples/dark/chat-order-form.webp',
    label: 'Order Form',
    category: 'execution',
    component: withCardFrame(OrderContainer),
  },
  // 9) Accounts
  'account-cards': {
    key: 'account-cards',
    icon: LayoutDashboardIcon,
    src: '/images/examples/dark/account-overview.webp',
    label: 'Account Cards',
    category: 'portfolio',
    component: withCardFrame(withFullscreen(AccountCards)),
  },
  // 11) Economic Calendar
  'economic-calendar': {
    key: 'economic-calendar',
    icon: CalendarIcon,
    src: '/images/examples/dark/economic-calendar.webp',
    label: 'Economic Calendar',
    category: 'tools',
    component: withCardFrame(withFullscreen(EconomicCalendarWidget)),
  },
  // 12) Newsfeed
  newsfeed: {
    key: 'newsfeed',
    icon: NewspaperIcon,
    src: '/images/examples/dark/newsfeed.webp',
    label: 'News',
    category: 'tools',
    component: withCardFrame(withFullscreen(NewsfeedWidget)),
  },
  // 13) Risk Slides
  'risk-slides': {
    key: 'risk-slides',
    icon: ChartNetworkIcon,
    src: '/images/examples/dark/risk-slides.webp',
    label: 'Risk Slides',
    category: 'portfolio',
    component: withCardFrame(withFullscreen(RiskSlidesWidget)),
  },
  // 14) Data (Amberdata Analytics)
  data: {
    key: 'data',
    icon: DatabaseIcon,
    src: '/images/examples/dark/data-chart.webp',
    label: 'Data',
    category: 'market',
    component: withCardFrame(withFullscreen(DataWidget)),
  },
  // 15) Preset Manager
  'preset-manager': {
    key: 'preset-manager',
    icon: LayersIcon,
    src: '/images/examples/dark/order-preset.webp',
    label: 'Shortcuts',
    category: 'execution',
    component: withCardFrame(withFullscreen(PresetWidget)),
  },
  // 17) Quick Order (read-only spread/depth-ladder reference)
  'quick-order': {
    key: 'quick-order',
    icon: ZapIcon,
    src: '/images/examples/dark/quick-order.webp',
    label: 'Quick Order',
    category: 'execution',
    component: withCardFrame(withFullscreen(QuickOrderWidget)),
  },
  // 18) Options Analytics (ENG-613 — unified volume/trades/leaderboard widget) is
  // retired: its Volume/Open Interest/Trades/Strike Flow/Structure content all now
  // live as entries in the Data widget's picker (see DATA_WIDGET_OPTION_KINDS in
  // amberdata-data-widget.tsx), which reuses this widget's body components
  // (features/dashboard/components/widgets/options/analytics/bodies/). 'options-analytics'
  // is no longer registered here at all — same treatment as the earlier
  // 'options-volume'/'options-trades'/'options-leaderboard' retirement below it used
  // to describe: any widget instance still persisted under the old key falls through
  // `WidgetContent`'s `COMPONENT_REGISTRY[componentKey]` lookup to the existing
  // `DeletedWidget` fallback (features/dashboard/components/widget-content.tsx)
  // instead of a bespoke legacy shim.
  // 21) One Tap Buy (stream-deck-style instant market order buttons)
  'one-tap-buy': {
    key: 'one-tap-buy',
    icon: MousePointerClickIcon,
    src: '/images/examples/dark/one-tap-order.webp',
    label: 'One Tap Buy',
    category: 'execution',
    component: withCardFrame(withFullscreen(OneTapBuyWidget)),
  },
  // 22) Spreader (live cross-venue spread matrix; click a quote to prefill a Spreader order)
  spreader: {
    key: 'spreader',
    icon: SplitIcon,
    src: '/images/examples/dark/spreader.webp',
    label: 'Spreader',
    category: 'execution',
    component: withCardFrame(withFullscreen(SpreaderWidget)),
  },
}

// Helper to get component config by label (for Add Components button)
export function getComponentConfigByLabel(label: string): ComponentConfig | undefined {
  return Object.values(COMPONENT_REGISTRY).find((config) => config.label === label)
}
